import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface User {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string;
  role?: string;
  hasPassword?: boolean;
}

interface OriginalAuth {
  user: User;
  accessToken: string;
  refreshToken: string;
}

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isImpersonating: boolean;
  originalAuth: OriginalAuth | null;
  setAuth: (data: { user: User; accessToken: string; refreshToken: string }) => void;
  setTokens: (data: { accessToken: string; refreshToken: string }) => void;
  setUser: (user: User) => void;
  logout: () => void;
  startImpersonation: (data: { user: User; accessToken: string }) => void;
  stopImpersonation: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isImpersonating: false,
      originalAuth: null,

      setAuth: ({ user, accessToken, refreshToken }) =>
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
        }),

      setTokens: ({ accessToken, refreshToken }) =>
        set({
          accessToken,
          refreshToken,
        }),

      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
        }),

      logout: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isImpersonating: false,
          originalAuth: null,
        }),

      startImpersonation: ({ user, accessToken }) => {
        const state = get();
        set({
          originalAuth: {
            user: state.user!,
            accessToken: state.accessToken!,
            refreshToken: state.refreshToken!,
          },
          user,
          accessToken,
          refreshToken: null,
          isAuthenticated: true,
          isImpersonating: true,
        });
      },

      stopImpersonation: () => {
        const state = get();
        if (state.originalAuth) {
          set({
            user: state.originalAuth.user,
            accessToken: state.originalAuth.accessToken,
            refreshToken: state.originalAuth.refreshToken,
            isAuthenticated: true,
            isImpersonating: false,
            originalAuth: null,
          });
        }
      },
    }),
    {
      // IMPORTANT: marketing/src/components/Header.astro reads this key to determine auth state
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        isImpersonating: state.isImpersonating,
        originalAuth: state.originalAuth,
      }),
    },
  ),
);
